import React, { useState, useEffect, useContext, useRef } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import documentData from "../../../../../api/documents";
import viewDocumentsBulkLink from "../../../../../api/viewDocumentsBulkLink";
import { AuthContext } from "../../../../../context/Auth/AuthContext";
import {
  ChevronDownIcon,
  CaretSortIcon,
} from "@radix-ui/react-icons";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../shadcn-components/ui/tooltip";

import { Button } from "../../../../../shadcn-components/ui/button";
import { Checkbox } from "../../../../../shadcn-components/ui/checkbox";
import PaginationSection from "../../../../common/PaginationSection";
import TableSkeleton from "../../../../common/TableSkeleton";
import DataToolbar from "../../../../common/DataToolbar";
import { createStickyColumns } from "../../../../common/stickyColumns";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "../../../../../shadcn-components/ui/dropdown-menu";
import { Input } from "../../../../../shadcn-components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../shadcn-components/ui/table";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../shadcn-components/ui/select";

import DeleteDocumentPopup from "../../../../document-table/DeleteDocumentPopup";
import CopyLinkPopup from "../CopyLinkPopup";
import ShareDocumentPopup from "../ShareDocumentPopup";
import DocumentViewer from "../DocumentViewer";
import baseUrl from "../../../../../config/baseUrl";
import "../../../../../styles/DocumentTable.css";
import { fileIcon } from "../../../../common/FileIcons";
import CategoryChip from "../../../../common/CategoryChip";
import viewDocumentLink from "../../../../../api/viewDocumentLink";
import BulkDeletePopup from "../../../../document-table/BulkDeletePopup";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../../../../utils/toast";
// import { Tooltip } from "@mui/material";
import { BsFillCloudArrowUpFill } from "react-icons/bs";
import { TbCircleCheck } from "react-icons/tb";
import { TiDeleteOutline } from "react-icons/ti";
import { useDebounce } from "use-debounce";
import editDocument from "../../../../../api/editDocument";
import { Edit2 } from "lucide-react";
import { useRoleContext } from "../../../../../context/RolesContext";
import { convertToUTC, formatDate } from "../../../../../utils/formatDate";
import { format } from "date-fns";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

const DocumentTable = ({
  clientID,
  selectedDocCategoryType,
  changeDocumentViewerState,
  documentUpdatedToTrue,
  setDocumentUpdatedToTrue,
  subCategoryList,
  currentUploadPage,
  setCurrentUploadPage,
  actionButton,
}) => {
  const [subCategoryUpdated, setSubCategoryUpdated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [documentDeleted, setDocumentDeleted] = useState(0);
  const [bulkDeleteDocument, setBulkDeleteDocument] = useState(false);
  const [bulkDeleteDocumentIds, setBulkDeleteDocumentIds] = useState();
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState({
    completed: 0,
    total: 0,
  });

  const [documentResponse, setDocumentResponse] = useState([]);
  const [editingCell, setEditingCell] = useState(null);

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);

  const [selectAllChecked, setSelectAllChecked] = useState();
  const [showFilterButton, setShowFilterButton] = useState(false);

  let docCategory = `&category_id=${selectedDocCategoryType.toLowerCase()}`;
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const prevStateRef = useRef({
    updatedAt,
    debouncedGlobalFilter,
    columnFilters,
  });

  const setDocumentDeletedToTrue = () => {
    setDocumentDeleted((count) => count + 1);
  };

  const { token, user_id, is_admin } = useContext(AuthContext);

  //Role Based Permissions

  // Function to check if the user has a specific permission.
  // Admin users ("is_admin" === "1") automatically have access to all permissions.
  const { permissions } = useRoleContext();
  const hasPermission = (permission) => {
    if (is_admin === "1") {
      return true;
    }

    return permissions.includes(permission);
  };

  // Permission flags for various user actions, granting access based on user roles or admin status.
  // Admin users have all permissions by default.
  const canShareDocuments = hasPermission("share_all_documents");
  const canDeleteDocument = hasPermission("delete_all_documents");
  const canDownload = hasPermission("download_all_documents");
  const canViewDocument = hasPermission("view_all_documents");
  const canCopyDocument = hasPermission("copy_all_documents");

  //To hide the action column if there is not a single permission
  const shouldShowActionColumn =
    canShareDocuments ||
    canDeleteDocument ||
    canViewDocument ||
    canCopyDocument;

  const documentResponseFunc = async () => {
    setLoading(true);
    const offset = currentUploadPage - 1;
    const filterParams = {};

    columnFilters.forEach((filterItem) => {
      filterParams[filterItem.id] = filterItem.value;
    });
    const sortParam = sorting
      .map((sortItem) => {
        return `${sortItem.id}=${sortItem.desc ? "desc" : "asc"}`;
      })
      .join(",");
    const params = {
      contact_id: clientID,
      isAdmin: is_admin,
      userId: user_id,
      limit: pageSize,
      offset: offset,
      ...filterParams,
      global_search: debouncedGlobalFilter,
      sort: sortParam,
      ...(createdAt ? { created_at: convertToUTC(createdAt) } : {}),
      ...(updatedAt ? { updated_at: convertToUTC(updatedAt) } : {}),
    };
    const queryString = new URLSearchParams(params).toString();
    const url = `${baseUrl}/api/get-all-doc-data?${queryString}${docCategory}`;

    const response = await documentData({ token, api: url });

    if (response?.code === 200) {
      const rowData = response.data[0].document.map((row) => ({
        ...row,
        id: row.document_id,
      }));

      setDocumentResponse(rowData);
      setTotalPages(Math.ceil(response?.data[0]?.alldocumentcount / pageSize));
    } else {
      setTotalPages(1);
      setDocumentResponse([]);
    }
    setLoading(false);
  };

  const openLinkInNewTab = async (rowData) => {
    const downloadLinkFormResponse = await viewDocumentLink({
      token: token,
      docId: rowData.document_id,
    });

    if (
      downloadLinkFormResponse?.code === 200 &&
      downloadLinkFormResponse?.data[0]?.pdflink
    ) {
      const link = downloadLinkFormResponse?.data[0].pdflink;
      //window.open(`${link}`);
      changeDocumentViewerState(
        rowData.document_id,
        rowData.document_request_id,
        downloadLinkFormResponse?.data[0].document_status,
        `${link}`,
        rowData.document_name,
        rowData.category_id,
        rowData.document_desc
      );
    } else {
      showError("Download URL does not Exists");
    }
  };

  const handleBulkAction = async (actionType) => {
    const selectedRowsData = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);

    if (actionType === "delete") {
      const ids = selectedRowsData.map((row) => row.document_id).join(",");
      setBulkDeleteDocument(true);
      setBulkDeleteDocumentIds(ids);
    } else if (actionType === "download") {
      handleBulkDownload(selectedRowsData);
    }
  };

  const handleBulkDownload = async (selectedRowsData) => {
    if (!selectedRowsData.length) return;

    setBulkDownloading(true);
    setBulkDownloadProgress({ completed: 0, total: selectedRowsData.length });

    const zip = new JSZip();
    const usedNames = new Set();

    try {
      const docIds = selectedRowsData.map((row) => row.document_id);

      const bulkResponse = await viewDocumentsBulkLink({ token, docIds });
      const links = bulkResponse?.data || [];

      if (!links.length) {
        showError(
          "Could not retrieve download links for the selected documents",
        );
        return;
      }

      const rowById = Object.fromEntries(
        selectedRowsData.map((row) => [row.document_id, row]),
      );

      await Promise.all(
        links.map(async (linkInfo) => {
          try {
            const { document_id, pdflink } = linkInfo;
            if (!pdflink) {
              console.error(`No download link for document ${document_id}`);
              return;
            }

            const fileResponse = await fetch(
              `${baseUrl}/api/download-document-proxy?document_id=${document_id}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!fileResponse.ok) {
              console.error(`Failed to fetch document ${document_id}`);
              return;
            }
            const blob = await fileResponse.blob();

            let fileName =
              rowById[document_id]?.document_name ||
              linkInfo.document_file_name ||
              `document-${document_id}`;

            if (usedNames.has(fileName)) {
              const dotIndex = fileName.lastIndexOf(".");
              const base =
                dotIndex !== -1 ? fileName.slice(0, dotIndex) : fileName;
              const ext = dotIndex !== -1 ? fileName.slice(dotIndex) : "";
              fileName = `${base}-${document_id}${ext}`;
            }
            usedNames.add(fileName);

            zip.file(fileName, blob);
          } catch (err) {
            console.error(
              `Error processing document ${linkInfo.document_id}:`,
              err,
            );
          } finally {
            setBulkDownloadProgress((prev) => ({
              ...prev,
              completed: prev.completed + 1,
            }));
          }
        }),
      );

      if (Object.keys(zip.files).length === 0) {
        showError("Could not download any of the selected documents");
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `documents-${Date.now()}.zip`);
      showSuccess("Documents downloaded successfully");
    } catch (err) {
      showError("Failed to download selected documents");
    } finally {
      setBulkDownloading(false);
      table.resetRowSelection();
      setBulkDownloadProgress({ completed: 0, total: 0 });
    }
  };

  const handleSubCategorySave = async (selectedOption, document_id) => {
    const response = await editDocument({
      token: token,
      user_id: user_id,
      document_id: document_id,
      document_sub_category: selectedOption,
    });

    if (response.code === 200) {
      setLoading(false);
      setDocumentUpdatedToTrue((count) => count + 1);
      setEditingCell(null);
      setSubCategoryUpdated((prev) => !prev);
      showSuccess("Sub Category updated successfully");
    } else {
      setLoading(false);
      showError("Error while updating document");
    }
  };

  useEffect(() => {
    const currentState = {
      createdAt,
      updatedAt,
      debouncedGlobalFilter,
      columnFilters,
    };

    // If filters or sorting change, reset page to 1
    if (JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState)) {
      if (currentUploadPage !== 1) {
        setCurrentUploadPage(1);
        return; // Avoid double API call
      }
    }

    prevStateRef.current = currentState;

    documentResponseFunc();
    setSelectAllChecked(false);
  }, [
    selectedDocCategoryType,
    clientID,
    documentDeleted,
    bulkDeleteDocument,
    documentUpdatedToTrue,
    currentUploadPage,
    columnFilters,
    debouncedGlobalFilter,
    sorting,
    pageSize,
    subCategoryUpdated,
    createdAt,
    updatedAt,
  ]);

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <div className="flex items-center">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
          {(table.getIsSomePageRowsSelected() ||
            table.getIsAllPageRowsSelected()) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="p-0 !border-none !shadow-none bg-transparent"
                >
                  <ChevronDownIcon className="" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => handleBulkAction("download")}
                  disabled={!canDownload || bulkDownloading}
                >
                  {bulkDownloading ? "Downloading..." : "Download"}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleBulkAction("delete")}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => {
            setSelectAllChecked(true);
            row.toggleSelected(!!value);
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "document_type",
      filterable: true,
      displayName: "Type",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="p-0"
          >
            Type
            <CaretSortIcon className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="w-full flex items-center">
          {fileIcon[row.getValue("document_type").toLowerCase()]}
        </div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "document_name",
      displayName: "Document Name",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Document Name
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const document_name = row.getValue("document_name");
        return (
          <div className="wrap-text capitalize text-left ">{document_name}</div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "document_request_setting_name",
      displayName: "Linked Request",
      filterable: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Linked Request
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        let document_request_name = row.getValue(
          "document_request_setting_name"
        );
        const additional_doc_name = row.original.additional_doc_name; // Assuming additional_doc_name is part of row.original

        // Function to capitalize the first letter of a string
        const capitalizeFirstLetter = (string) => {
          return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
        };

        // Check if the document_request_name is "Additional Document"
        if (
          document_request_name === "Additional Document" &&
          additional_doc_name
        ) {
          // Capitalize the first letter of additional_doc_name
          const capitalized_additional_doc_name =
            capitalizeFirstLetter(additional_doc_name);
          document_request_name = `${document_request_name} - ${capitalized_additional_doc_name}`;
        }

        return (
          <div className="wrap-text limited-width flex text-left items-center justify-start">
            <span className="flex items-center justify-center case-doc-tables mr-4">
              {row.original.document_request_status ? (
                row.original.document_request_status?.toLowerCase() ===
                "requested" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="request-doc-request-icon request-doc-icon">
                          <BsFillCloudArrowUpFill />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Requested</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : row.original.document_request_status.toLowerCase() ===
                  "uploaded" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="request-doc-upload-icon request-doc-icon">
                          <BsFillCloudArrowUpFill />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Uploaded</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : row.original.document_request_status.toLowerCase() ===
                  "approved" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="request-doc-approved-icon request-doc-icon">
                          <TbCircleCheck />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Approved</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : row.original.document_request_status.toLowerCase() ===
                  "rejected" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span className="request-doc-rejected-icon request-doc-icon">
                          <TiDeleteOutline />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-600">
                        <p>Rejected</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null
              ) : null}
            </span>
            {document_request_name}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    // {
    //   accessorKey: "document_status",
    //   displayName: "Status",
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       className="p-0"
    //     >
    //       Status
    //       <CaretSortIcon className="ml-2 h-4 w-4" />
    //     </Button>
    //   ),
    //   cell: ({ row }) => (
    //     <div className="text-left">
    //       {row.getValue("document_status").toLowerCase() === "active"
    //         ? "Active"
    //         : "Inactive"}
    //     </div>
    //   ),
    //   filterFn: (row, id, value) => {
    //     return value.includes(row.getValue(id));
    //   },
    // },
    {
      accessorKey: "doc_category",
      displayName: "Category",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Category
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <CategoryChip
          label={row.getValue("doc_category")}
          color={row.original.bg_color}
        />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "document_subtype",
      displayName: "Sub Category",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Sub Category
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const isEditing =
          editingCell?.rowId === row.id &&
          editingCell?.columnId === "document_subtype";
        return (
          <div className="capitalize cursor-pointer">
            {isEditing ? (
              <select
                className="w-full p-2 border border-gray-300 rounded-sm outline-none"
                defaultValue={row.original.document_sub_category}
                selected={row.original.document_sub_category}
                onBlur={() => setEditingCell(null)}
                onChange={(e) => {
                  handleSubCategorySave(
                    e.target.value,
                    row?.original?.document_id
                  );
                }}
                autoFocus
              >
                {subCategoryList.map((category) => (
                  <option
                    value={category.field_setting_id}
                    key={category.field_setting_id}
                  >
                    {category.fields_name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="flex gap-2 items-center">
                {row.getValue("document_subtype")}
                <Edit2
                  size={16}
                  className="hover:fill-[#2b93c8d0] hover:stroke-[#2b93c8d0]"
                  onClick={() =>
                    setEditingCell({
                      rowId: row.id,
                      columnId: "document_subtype",
                    })
                  }
                />
              </span>
            )}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },

    {
      accessorKey: "created_at",
      displayName: "Created At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Created At
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const createdAt = row.getValue("created_at");
        return (
          <div className="capitalize text-wrap">{formatDate(createdAt)}</div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "updated_at",
      displayName: "Modified At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Modified At
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const updated = row.getValue("updated_at");
        return (
          <div className="capitalize text-wrap">{formatDate(updated)}</div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "actions",
      displayName: "Actions",
      header: ({ column }) => {
        if (!shouldShowActionColumn) {
          return null;
        }
        return (
          <Button variant="ghost" className="p-0 !cursor-default">
            Actions
          </Button>
        );
      },
      enableHiding: false,
      cell: ({ row }) => {
        return (
          <div className="flex gap-1.5">
            {canCopyDocument && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span>
                      <CopyLinkPopup tableValues={row.original} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-600">
                    <p>Copy</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {canShareDocuments && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span>
                      <ShareDocumentPopup tableValues={row.original} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-600">
                    <p>Share</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {canViewDocument && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span>
                      <DocumentViewer
                        tableValues={row.original}
                        changeDocumentViewerState={changeDocumentViewerState}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-600">
                    <p>View</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {canDeleteDocument && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span>
                      <DeleteDocumentPopup
                        setDocumentDeletedToTrue={setDocumentDeletedToTrue}
                        data={row.original}
                        setRowSelection={setRowSelection}
                        setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-600">
                    <p>Delete</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
  ];

  // Pins the leading "select"/"Type" columns to the left edge and the
  // trailing "actions" column to the right edge, so only the data-heavy
  // middle columns scroll horizontally. Widths are fixed so the header and
  // body cells for a given column line up at the same left/right offset.
  const { getStickyCellProps } = createStickyColumns({
    left: { select: 48, document_type: 70 },
    right: ["actions"],
  });

  const table = useReactTable({
    data: documentResponse,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pageSize: 20,
    },
    manualPagination: true,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pageSize,
    },

    globalFilterFn: (rows, columnFilters) => {
      const [globalFilter] = columnFilters.filter(
        (filter) => filter.id === "global"
      );
      if (!globalFilter || !globalFilter.value) return rows;

      return rows.filter((row) => {
        const name = row.original.name; // Assuming 'name' is the column key
        return name.includes(globalFilter.value);
      });
    },
    onGlobalFilterChange: (filterValue) => {
      setGlobalFilter(filterValue); // Update the global filter state
    },
  });

  return (
    <>
      {bulkDeleteDocument ? (
        <BulkDeletePopup
          setDocumentDeletedToTrue={setDocumentDeletedToTrue}
          Docbulkid={bulkDeleteDocumentIds}
          bulkDeleteDocument={bulkDeleteDocument}
          setBulkDeleteDocument={setBulkDeleteDocument}
          setRowSelection={setRowSelection}
          setDocumentUpdatedToTrue={setDocumentUpdatedToTrue}
        />
      ) : null}
      {bulkDownloading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="flex flex-col items-center gap-3 rounded-lg bg-white px-8 py-6 shadow-lg">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
            <p className="text-sm font-medium text-gray-700">
              Preparing your download...
            </p>
          </div>
        </div>
      )}
      <div className="table-content">
        <div className="w-full bg-white px-2">
          <div className="flex justify-between items-center py-3 flex-wrap gap-4">
            <Input
              placeholder="Search By Document Name..."
              value={globalFilter}
              onChange={(event) => table.setGlobalFilter(event.target.value)}
              className="max-w-sm"
            />
            <div className="flex gap-2">
              {actionButton}
              <Button
                variant="outline"
                className=""
                onClick={() => setShowFilterButton(!showFilterButton)}
              >
                <FilterListIcon sx={{ fontSize: 18 }} className="mr-1" />
                Filter
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="ml-auto">
                    <ViewColumnIcon sx={{ fontSize: 18 }} className="mr-1" />
                          Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {column.columnDef.displayName}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {showFilterButton && (
            <DataToolbar
              table={table}
              apiUrl="/api/get-document-filter-name"
              // docCategory={docCategory}
              clientID={clientID}
              createdAt={createdAt}
              updatedAt={updatedAt}
              setCreatedAt={setCreatedAt}
              setUpdatedAt={setUpdatedAt}
            />
          )}

          <div className="rounded-md border">
            {loading ? (
              <TableSkeleton />
            ) : (
              <Table>
                <TableHeader className="pb-4 sticky top-0 bg-white z-[1]">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const sticky = getStickyCellProps(
                          header.column.id,
                          true,
                        );
                        return (
                          <TableHead key={header.id} style={sticky.style}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const sticky = getStickyCellProps(cell.column.id);
                          return (
                            <TableCell
                              key={cell.id}
                              className="py-1"
                              style={sticky.style}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        {loading ? <TableSkeleton /> : "no records found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex items-center justify-between space-x-2 py-2 flex-wrap gap-4">
            <div className="flex items-center gap-5">
              <div className="flex-1 text-sm text-muted-foreground">
                {table.getFilteredSelectedRowModel().rows.length} of{" "}
                {table.getFilteredRowModel().rows.length} row(s) selected.
              </div>
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium">Rows per page</p>
                <Select
                  value={`${table.getState().pageSize}`}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setCurrentUploadPage(1);
                  }}
                >
                  <SelectTrigger className="w-auto">
                    <SelectValue placeholder={table.getState().pageSize} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {[10, 20, 30, 40, 50].map((pageSize) => (
                        <SelectItem key={pageSize} value={`${pageSize}`}>
                          {pageSize}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-x-2">
              <PaginationSection
                setCurrentPage={setCurrentUploadPage}
                totalPages={totalPages}
                currentPage={currentUploadPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentTable;
